import React, { useState, useRef, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import Button from '@SiteComponents/Button'

import {
  getUseLightTheme,
} from '@SiteModules/User/selectors'

import {
  getCurrentAccountStripeTaxPercent,
  getCurrentAccountStripeCurrency,
  getCurrentAccountId,
  getCurrentAccountCreditPricePerHour,
} from '@SiteModules/Account/selectors'

import {
  startCheckoutSession,
} from '@SiteModules/Credit/actions'

import './CreditsPurchase.scss'

const MIN_HOURS = 0
const MAX_HOURS = 100
const COLOR_BEFORE_HANDLE = 'rgba(147, 99, 247, 1)' // var(--bg-accent-purple)
const COLOR_BEFORE_HANDLE_LIGHT = 'rgba(187, 159, 252, 1)' // var(--bg-accent-purple)
const COLOR_AFTER_HANDLE = 'rgba(35, 32, 42, 1)' // var(--bg-surface)
const COLOR_AFTER_HANDLE_LIGHT = 'rgba(255, 255, 255, 1)' // var(--bg-surface)

const CreditsPurchase: React.FC = () => {
  const dispatch = useDispatch()
  const [hours, setHours] = useState(0)
  const [paying, setPaying] = useState(false)

  const useLightTheme = useSelector(getUseLightTheme)
  const currentAccountId = useSelector(getCurrentAccountId)
  const currentAccountStripeTaxPercent = useSelector(getCurrentAccountStripeTaxPercent) || 0
  const currentAccountStripeCurrency = useSelector(getCurrentAccountStripeCurrency)
  const currentAccountCreditPricePerHour = useSelector(getCurrentAccountCreditPricePerHour)

  const shouldAddVat = currentAccountStripeTaxPercent > 0
  const currencySymbol = currentAccountStripeCurrency === 'USD' ? '$' : '€'
  const noHours = hours === 0

  const price = hours * currentAccountCreditPricePerHour
  const vat = price * currentAccountStripeTaxPercent / 100
  const total = price + vat

  const barRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    updateBarBackground(hours)

    if (!barRef.current) { return }

    barRef.current.value = hours.toString()
  }, [hours])

  const handleProceedToPayment = () => {
    setPaying(true)
    dispatch(startCheckoutSession({ hours, accountId: currentAccountId }))
  }

  const handleKeyDownHours = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHours(prev => Math.min(MAX_HOURS, prev + 1))
    } else if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHours(prev => Math.max(MIN_HOURS, prev - 1))
    }
  }

  const onInputBar = (e: React.FormEvent<HTMLInputElement>) => {
    const newTargetHours = parseInt(e.currentTarget.value, 10)
    updateBarBackground(newTargetHours)
    setHours(newTargetHours)
  }

  const updateBarBackground = (newHours: number) => {
    if (!barRef.current) { return }

    const newPercentage = ((newHours - MIN_HOURS) / (MAX_HOURS - MIN_HOURS)) * 100
    const colorAfterHandle = useLightTheme ? COLOR_AFTER_HANDLE_LIGHT : COLOR_AFTER_HANDLE
    const colorBeforeHandle = useLightTheme ? COLOR_BEFORE_HANDLE_LIGHT : COLOR_BEFORE_HANDLE
    barRef.current.style.background = `linear-gradient(to right, ${colorBeforeHandle} 0%, ${colorBeforeHandle} ${newPercentage}%, ${colorAfterHandle} ${newPercentage}%, ${colorAfterHandle} 100%)`
  }

  const handleChangeHours = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStringValue = (e.target as HTMLInputElement).value
    let newValue = parseInt(newStringValue, 10)

    if (newStringValue === '' || newValue < MIN_HOURS) {
      newValue = MIN_HOURS
    } else if (newValue > MAX_HOURS) {
      newValue = MAX_HOURS
    }
    setHours(newValue)
  }

  const renderHeader = () => {
    return (
      <div className="CreditsPurchase__header">
        Top-up credits
        <div className="CreditsPurchase__header-explanation">
          Purchase additional credits to continue using our services. Select the number of credits you require.
        </div>
      </div>
    )
  }

  const renderConfiguration = () => {
    return (
      <div className="CreditsPurchase__configuration">
        <div className="CreditsPurchase__configuration-header">
          Configuration
        </div>
        <div className="CreditsPurchase__configuration-explanation">
          Select the amount of hours of credits you wish to purchase
        </div>
        {renderCreditSelector()}
        {renderInformation()}
        {renderSummary()}
        {renderUnitPrice()}
      </div>
    )
  }

  const renderCreditSelector = () => {
    return (
      <React.Fragment>
        <div className="CreditsPurchase__value-wrapper">
          <span className="CreditsPurchase__value">Number of hours</span>
          <input
            type="number"
            min={MIN_HOURS.toString()}
            max={MAX_HOURS.toString()}
            value={hours.toString()}
            onChange={handleChangeHours}
            onKeyDown={handleKeyDownHours}
          />
        </div>
        <div
          className="CreditsPurchase__bar"
        >
          <input
            className="CreditsPurchase__bar-input"
            type="range"
            onInput={onInputBar}
            min={MIN_HOURS}
            max={MAX_HOURS}
            step={1}
            defaultValue={hours}
            ref={barRef}
          />
        </div>
        <div className="CreditsPurchase__bar-limits">
          <div className="CreditsPurchase__bar-limits-lower">
            {MIN_HOURS}
          </div>
          <div className="CreditsPurchase__bar-limits-upper">
            {MAX_HOURS}
          </div>
        </div>
      </React.Fragment>
    )
  }

  const renderInformation = () => {
    return (
      <div className="CreditsPurchase__information">
        <div className="CreditsPurchase__information-bar"/>
        <div className="CreditsPurchase__information-content">
          <div className="CreditsPurchase__information-header">
            Information
          </div>
          <div className="CreditsPurchase__information-explanation">
            Credits are valid for one year as long as you have an active subscription.
          </div>
        </div>
      </div>
    )
  }

  const renderSummary = () => {
    return (
      <div className="CreditsPurchase__summary">
        <div className="CreditsPurchase__summary-header">
          Summary
        </div>
        <div className="CreditsPurchase__summary-hours">
          <div className="CreditsPurchase__summary-hours-number">
            {hours} hours
          </div>
          <div className="CreditsPurchase__summary-hours-price">
            {price} {currencySymbol}
          </div>
        </div>
        {shouldAddVat && (
          <div className="CreditsPurchase__summary-vat">
            <div className="CreditsPurchase__summary-vat-number">
              Vat ({currentAccountStripeTaxPercent}%)
            </div>
            <div className="CreditsPurchase__summary-vat-price">
              {vat} {currencySymbol}
            </div>
          </div>
        )}
        <div className="CreditsPurchase__summary-total">
          <div className="CreditsPurchase__summary-total-text">
            Total
          </div>
          <div className="CreditsPurchase__summary-total-price">
            {total} {currencySymbol}
          </div>
        </div>
      </div>
    )
  }

  const renderUnitPrice = () => {
    return (
      <div className="CreditsPurchase__unit-price">
        Unit price: {currencySymbol}{currentAccountCreditPricePerHour} per hour of credits
      </div>
    )
  }

  const renderBottom = () => {
    return (
      <div className="CreditsPurchase__bottom">
        <Button
          styleType="brand"
          size="large"
          caption="Proceed to payment"
          onClick={handleProceedToPayment}
          disabled={paying || noHours}
        />
        <div className="CreditsPurchase__bottom-info">
          Secure payment with Stripe
        </div>
      </div>
    )
  }

  return (
    <div className="CreditsPurchase">
      {renderHeader()}
      {renderConfiguration()}
      {renderBottom()}
    </div>
  )
}

export default CreditsPurchase
